// The travel row at the START of a schedule day (2026-10-16). The schedule
// already shows the leg between two events of the same day; a day's first
// event had none. Now:
//   - same city as the day before, hotel known → "From <hotel>", with the
//     estimated distance/time to the day's first place;
//   - same city, no hotel → "From your hotel" (no numbers — there's nothing
//     to measure from), tap to set the hotel;
//   - arriving from another city → "From <previous city>", measured from that
//     city's hotel if known, else the previous day's last place;
//   - the trip's first day → nothing (where the traveller comes from isn't
//     known).
// When the trip has destination dates, the ARRIVAL time decides which: a
// city reached the evening before (Florence from 21:00 on day 2) starts day 3
// as a normal Florence day (hotel row), and day 2 ends with "To Florence"
// (dayEndLeg). Arriving that morning keeps the "From Milan" row on day 3.
// Without destination dates it's worked out from the events alone.
// A hotel is any place in the guide with a Hotel/Lodging category, in the
// day's city (or within HOTEL_NEAR_KM of its first place); with several, the
// one last checked into (latest event before the day), else the nearest.

import { DateTime } from "luxon";
import Common from "./Common";

export const HOTEL_NEAR_KM = 30;
const SAME_CITY_KM = 30;

const norm = (s) => String(s || "").trim().toLowerCase();
const firstPlaceOf = (event, places) => {
  const id = String(event?.Places || "").split(",")[0].trim();
  return id ? places.getPlace(id) : null;
};
const km = (a, b) => Common.haversineMeters(a.Lat, a.Longi, b.Lat, b.Longi) / 1000;

// The guide's hotel category ids: named "Hotel" or "Lodging".
export const hotelCategoryIds = (categories) =>
  new Set((categories || []).filter((c) => /^(hotel|hotels|lodging|accommodation)$/i.test(String(c.name || "").trim())).map((c) => String(c.id)));

export const isHotel = (place, hotelIds) => !!place && (place.Category || []).some((id) => hotelIds.has(String(id)));

// The hotel for `city` near `near`; events (all of the schedule's) decide
// which one is "that night's" when there are several.
export function findHotel({ places, categories, city, near, nightOf, events }) {
  const hotelIds = hotelCategoryIds(categories);
  if (hotelIds.size === 0) return null;
  const list = (places.placesList || []).filter((p) => isHotel(p, hotelIds) && !p.Hidden &&
    ((city && norm(p.City) === norm(city)) || (near && km(p, near) <= HOTEL_NEAR_KM)));
  if (list.length <= 1) return list[0] || null;
  if (nightOf && events) {
    // The hotel you last checked into: the one with the latest event before
    // the day starts (a Hotel stop or an imported booking adds a check-in
    // event). Moving from hotel A to B mid-stay switches on the day after B.
    let best = null, bestAt = null;
    for (const e of events) {
      const s = DateTime.fromISO(e.Start, { zone: "utc" });
      if (!(s < nightOf)) continue;
      const ids = String(e.Places || "").split(",").map((x) => x.trim());
      const h = list.find((x) => ids.includes(String(x.ID)));
      if (h && (!bestAt || s > bestAt)) { best = h; bestAt = s; }
    }
    if (best) return best;
  }
  return near ? list.slice().sort((a, b) => km(a, near) - km(b, near))[0] : list[0];
}

// What to show above the first event of the day starting `dayDate` (a UTC
// DateTime at any time that day). `events` = all events of the schedule
// shown. Returns null, or { kind: "hotel" | "noHotel" | "arrival", from,
// to, fromCity, city, travel }.
// The destination whose stay covers the start of `dayStart` — arrived at or
// before it, leaving after it — or null (no dates, or arriving that day).
export function destinationAt(destinations, dayStart) {
  const list = (destinations || []).filter((d) => d && d.city && d.dateStart && d.dateEnd && d.dateStart.isValid !== false);
  return list.filter((d) => d.dateStart <= dayStart && d.dateEnd > dayStart).sort((a, b) => b.dateStart - a.dateStart)[0] || null;
}
// Only a destination with a real city counts — a country-level one ("Italy")
// spans several cities, so it can't say which one a place is in.
const inCity = (place, dest) => !!place && !!dest && norm(dest.city) !== norm(dest.country) && norm(place.City) === norm(dest.city);

export function dayStartLeg({ dayDate, firstEvent, events, places, categories, destinations }) {
  const to = firstPlaceOf(firstEvent, places);
  if (!to || !dayDate) return null;
  const dayStart = dayDate.startOf("day");
  const before = (events || [])
    .filter((e) => DateTime.fromISO(e.Start, { zone: "utc" }) < dayStart && firstPlaceOf(e, places))
    .sort((a, b) => DateTime.fromISO(a.Start, { zone: "utc" }) - DateTime.fromISO(b.Start, { zone: "utc" }));
  const prevEvent = before[before.length - 1];
  if (!prevEvent) return null; // the trip's first day
  const prev = firstPlaceOf(prevEvent, places);
  const hotelIds = hotelCategoryIds(categories);
  // Already in the day's city when it starts (arrived the evening before,
  // per the destination dates) counts as the same city, wherever the last
  // event was.
  const here = destinationAt(destinations, dayStart);
  const sameCity = (to.City && prev.City && norm(to.City) === norm(prev.City)) || km(prev, to) <= SAME_CITY_KM || inCity(to, here);
  if (sameCity) {
    if (isHotel(to, hotelIds)) return null; // the day starts at the hotel itself
    const hotel = findHotel({ places, categories, city: to.City, near: to, nightOf: dayStart, events });
    if (!hotel) return { kind: "noHotel", to, city: to.City || "" };
    return { kind: "hotel", from: hotel, to, city: to.City || "", travel: Common.estimateTravel(hotel, to) };
  }
  const prevHotel = findHotel({ places, categories, city: prev.City, near: prev, nightOf: dayStart, events });
  const from = prevHotel || prev;
  return { kind: "arrival", from, to, fromCity: prev.City || from.Name, city: to.City || "", travel: Common.estimateTravel(from, to) };
}

// The row under a day's LAST event when the traveller moves to the next city
// that evening (by the destination dates): "To Florence", from the last place
// to the next city's first scheduled place. Null otherwise — including when
// that city already has events the same evening (the leg between those
// events shows already).
export function dayEndLeg({ dayDate, lastEvent, events, places, destinations }) {
  const from = firstPlaceOf(lastEvent, places);
  if (!from || !dayDate) return null;
  const dayStart = dayDate.startOf("day"), dayEnd = dayStart.plus({ days: 1 });
  const lastEnd = DateTime.fromISO(lastEvent.End || lastEvent.Start, { zone: "utc" });
  const next = (destinations || [])
    .filter((d) => d && d.city && d.dateStart && d.dateStart >= dayStart && d.dateStart < dayEnd && d.dateStart >= lastEnd && !inCity(from, d))
    .sort((a, b) => a.dateStart - b.dateStart)[0];
  if (!next) return null;
  const after = (events || [])
    .filter((e) => DateTime.fromISO(e.Start, { zone: "utc" }) >= dayEnd && inCity(firstPlaceOf(e, places), next))
    .sort((a, b) => DateTime.fromISO(a.Start, { zone: "utc" }) - DateTime.fromISO(b.Start, { zone: "utc" }))[0];
  const to = after ? firstPlaceOf(after, places) : null;
  if (!to) return null;
  return { kind: "departure", from, to, toCity: next.city, travel: Common.estimateTravel(from, to) };
}
